import type { BattleEvent } from '../game/types';

// Original, synthesized ambience and effects. No downloads or media permissions.
export class NavalAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private ambience: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private volume = 0.55;
  async unlock(): Promise<void> {
    try {
      if (!this.context) {
        this.context = new AudioContext();
        this.master = this.context.createGain();
        this.master.gain.value = this.volume * 0.38;
        this.master.connect(this.context.destination);
        this.noise = this.context.createBuffer(1, this.context.sampleRate * 3, this.context.sampleRate);
        const data = this.noise.getChannelData(0);
        for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
        const ocean = this.context.createBufferSource(); ocean.buffer = this.noise; ocean.loop = true;
        const filter = this.context.createBiquadFilter(); filter.type = 'lowpass'; filter.frequency.value = 380;
        this.ambience = this.context.createGain(); this.ambience.gain.value = 0.16;
        ocean.connect(filter).connect(this.ambience).connect(this.master); ocean.start();
      }
      await this.context.resume();
    } catch { /* A browser without audio still has a fully playable game. */ }
  }
  setVolume(value: number): void {
    this.volume = value;
    if (this.context && this.master) this.master.gain.setTargetAtTime(value * 0.38, this.context.currentTime, 0.08);
  }
  setPlaying(playing: boolean): void {
    if (this.context && this.ambience) this.ambience.gain.setTargetAtTime(playing ? 0.2 : 0.08, this.context.currentTime, 0.15);
  }
  events(events: BattleEvent[]): void {
    if (!this.context || !this.master || this.context.state !== 'running') return;
    for (const event of events) {
      if (event.type === 'fire') this.blast(event.faction === 'player' ? 1 : 0.5, 0.6, 95);
      else if (event.type === 'hit') this.blast(0.5, 0.24, 330);
      else if (event.type === 'sink') { this.blast(0.7, 1.3, 130); this.chime([110, 146.8], 0.35); }
      else if (event.type === 'result') this.chime(event.outcome === 'victory' ? [261.6, 329.6, 392, 523.2] : [220, 174.6, 130.8], 0.38);
    }
  }
  private blast(gain: number, duration: number, frequency: number): void {
    const ctx = this.context!; const now = ctx.currentTime;
    const src = ctx.createBufferSource(); src.buffer = this.noise;
    const filter = ctx.createBiquadFilter(); filter.type = 'lowpass'; filter.frequency.setValueAtTime(frequency * 6, now); filter.frequency.exponentialRampToValueAtTime(frequency, now + duration);
    const env = ctx.createGain(); env.gain.setValueAtTime(gain, now); env.gain.exponentialRampToValueAtTime(0.001, now + duration);
    src.connect(filter).connect(env).connect(this.master!); src.start(now); src.stop(now + duration);
    src.onended = () => { src.disconnect(); filter.disconnect(); env.disconnect(); };
    const tone = ctx.createOscillator(); const bass = ctx.createGain(); tone.frequency.setValueAtTime(110, now); tone.frequency.exponentialRampToValueAtTime(35, now + duration);
    bass.gain.setValueAtTime(gain * 0.5, now); bass.gain.exponentialRampToValueAtTime(0.001, now + duration);
    tone.connect(bass).connect(this.master!); tone.start(now); tone.stop(now + duration); tone.onended = () => { tone.disconnect(); bass.disconnect(); };
  }
  private chime(notes: number[], length: number): void {
    const ctx = this.context!;
    notes.forEach((frequency, index) => {
      const start = ctx.currentTime + index * 0.13; const oscillator = ctx.createOscillator(); oscillator.type = 'triangle'; oscillator.frequency.value = frequency;
      const envelope = ctx.createGain(); envelope.gain.setValueAtTime(0, start); envelope.gain.linearRampToValueAtTime(0.16, start + 0.015); envelope.gain.exponentialRampToValueAtTime(0.001, start + length);
      oscillator.connect(envelope).connect(this.master!); oscillator.start(start); oscillator.stop(start + length); oscillator.onended = () => { oscillator.disconnect(); envelope.disconnect(); };
    });
  }
  async dispose(): Promise<void> { await this.context?.close(); this.context = null; }
}
